import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import Utils from "../utils";

export default function AssistantChatDrawer(props) {
    const {
        drawerId,
        headerTitle,
        showChat,
        toggleChat,
        iconPath,
        chats,
        activeChat,
        onSelectChat,
        onSendMessage,
        onStartNewChat,
        isSending,
        statusLabels = {},
        thinkingMessage,
        errorMessage,
        labels: customLabels = {}
    } = props;

    const labels = {
        conversationsToggle: 'Conversaciones',
        newConversation: '+ Nueva conversación',
        emptyList: 'No hay conversaciones previas.',
        emptyStateNoChats: 'Comienza tu primera conversación escribiendo un mensaje.',
        emptyStateSelect: 'Selecciona una conversación o escribe para iniciar una nueva.',
        messagePlaceholderExisting: 'Escribe tu mensaje...',
        messagePlaceholderNew: 'Haz una pregunta para crear un nuevo chat...',
        sendButton: 'Enviar',
        ...customLabels
    };

    const [messageDraft, setMessageDraft] = useState('');
    const [showConversations, setShowConversations] = useState(false);
    const messagesRef = useRef(null);
    const keepPosRef = useRef({ pending: false, offsetFromBottom: 0 });

    const translateStatus = (status) => statusLabels[status] || status;

    const activeChatMessagesLength = activeChat ? activeChat.messages.length : 0;

    useLayoutEffect(() => {
        if (!messagesRef.current) return;
        if (!showChat) return;
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }, [activeChatMessagesLength, showChat]);

    useEffect(() => {
        if (!showChat) {
            setShowConversations(false);
        }
    }, [showChat]);

    const unreadCountForChat = (chat) => chat.messages.filter((message) => !message.read && message.from !== 'user').length;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        try {
            return new Intl.DateTimeFormat('es-CL', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }).format(new Date(timestamp));
        } catch (error) {
            return timestamp;
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const result = onSendMessage(messageDraft);
        Promise.resolve(result)
            .then(() => {
                setMessageDraft('');
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const disableInput = isSending || (activeChat && activeChat.status !== 'open');
    const handleStartNewChat = () => {
        setShowConversations(false);
        onStartNewChat();
    };

    useLayoutEffect(() => {
        if (!keepPosRef.current.pending) return;
        const el = messagesRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight - keepPosRef.current.offsetFromBottom;
        keepPosRef.current.pending = false;
    }, []);

    useLayoutEffect(() => {
        if (!messagesRef.current) return;
        if (keepPosRef.current.pending) return;
        if (showChat) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [showChat, activeChat && activeChat.id, activeChat && activeChat.messages.length]);

    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, []);

    const effectiveThinkingMessage = (activeChat && activeChat.thinking_tool_message) || thinkingMessage || '';

    const ThinkingIndicator = () => {
        if (!activeChat || activeChat.status !== 'thinking') return null;
        return (
            <div className="support-chat-message bot thinking">
                <div className="support-chat-message-avatar">
                    <img src={iconPath} alt="Assistant" />
                </div>
                <div className="support-chat-typing-wrapper">
                    <div className="support-chat-message-bubble typing">
                        <div className="support-chat-typing-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                    {effectiveThinkingMessage && (
                        <div className="support-chat-thinking-hint">
                            {effectiveThinkingMessage}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div id={drawerId} className={"assistant-drawer " + (showChat ? "open" : "")}> 
            <div className="assistant-drawer-header">
                {headerTitle}
                <div className="assistant-drawer-close" onClick={toggleChat} onKeyPress={() => {}}>&times;</div>
            </div>
            <div className="support-chat-content">
                <div className="support-chat-selector">
                    <div className="support-chat-selector-actions">
                        <button type="button" className="support-chat-selector-toggle" onClick={() => setShowConversations(!showConversations)}>
                            {labels.conversationsToggle}
                            <span className={showConversations ? 'open' : ''}>▾</span>
                        </button>
                        <button
                            type="button"
                            className={"support-chat-new-button " + (!activeChat ? 'active' : '')}
                            onClick={handleStartNewChat}
                        >
                            {labels.newConversation}
                        </button>
                    </div>
                    <div className={"support-chat-selector-list " + (showConversations ? 'visible' : '')}>
                        {chats.length === 0 && (
                            <div className="support-chat-selector-empty">
                                {labels.emptyList}
                            </div>
                        )}
                        {chats.map((chat) => {
                            const lastMessage = chat.messages[chat.messages.length - 1];
                            const unread = unreadCountForChat(chat);
                            const isActive = activeChat && chat.id === activeChat.id;
                            return (
                                <div
                                    key={chat.id}
                                    className={"support-chat-selector-item " + (isActive ? 'active' : '')}
                                    onClick={() => {
                                        onSelectChat(chat.id);
                                        setShowConversations(false);
                                    }}
                                    onKeyPress={() => {}}
                                >
                                    <div className="support-chat-selector-item-title">{chat.title || `Chat ${chat.id}`}</div>
                                    <div className="support-chat-selector-item-meta">
                                        <span className="support-chat-selector-item-date">{formatTimestamp(chat.last_message_at || chat.created_at)}</span>
                                        <span className={"support-chat-selector-item-status status-" + chat.status}>{translateStatus(chat.status)}</span>
                                    </div>
                                    {lastMessage && (
                                        <div className="support-chat-selector-item-preview">{lastMessage.content}</div>
                                    )}
                                    {unread > 0 && <div className="support-chat-selector-item-unread">{unread}</div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="support-chat-messages">
                    <div ref={messagesRef} className="support-chat-messages-list">
                        {activeChat && activeChat.messages.length > 0 ? (
                            <React.Fragment>
                                {activeChat.messages.map((message) => {
                                    const className = [
                                        'support-chat-message',
                                        message.from,
                                        (!message.read && message.from !== 'user') ? 'unread' : ''
                                    ].join(' ').trim();
                                    return (
                                        <div key={message.id} className={className}>
                                            <div className="support-chat-message-avatar">
                                                {message.from === 'user' ? (
                                                    <div className="support-chat-message-avatar-user">Yo</div>
                                                ) : (
                                                    <img src={iconPath} alt="Assistant" />
                                                )}
                                            </div>
                                            <div className="support-chat-message-bubble">
                                                <div className="support-chat-message-text" dangerouslySetInnerHTML={{__html: Utils.converter.makeHtml(message.content)}}></div>
                                                <div className="support-chat-message-meta">
                                                    <span>{message.created_at}</span>
                                                    {!message.read && message.from !== 'user' && <span className="support-chat-message-unread">●</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <ThinkingIndicator />
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div className="support-chat-empty">
                                    {chats.length === 0 ? labels.emptyStateNoChats : labels.emptyStateSelect}
                                </div>
                                <ThinkingIndicator />
                            </React.Fragment>
                        )}
                    </div>
                    <form className="support-chat-form" onSubmit={handleSubmit}>
                        {errorMessage && (
                            <div className="support-chat-error" role="alert">
                                {errorMessage}
                            </div>
                        )}
                        <textarea
                            value={messageDraft}
                            onChange={(event) => setMessageDraft(event.target.value)}
                            placeholder={activeChat ? labels.messagePlaceholderExisting : labels.messagePlaceholderNew}
                            disabled={disableInput}
                        />
                        <div className="support-chat-form-actions">
                            <button type="submit" disabled={disableInput || messageDraft.trim() === ''}>
                                {labels.sendButton}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
